import type { DirectiveOptions } from 'vue';
import { isInstanceOfSafeHtml } from '@mimir/common/util/safeHtmlBuilder';
import V3Tooltip from '@mimir/web/compat/V3Tooltip.vue';
import type { DirectiveBinding } from 'vue/types/options';
import Vue from 'vue';
import type { CombinedVueInstance } from 'vue/types/vue';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import debounce from 'lodash/debounce';

function createDiv() {
  const div = document.createElement('div');
  div.style.top = '0px';
  div.style.right = '0px';
  div.style.width = '7px';
  div.style.position = 'absolute';
  div.style.cursor = 'col-resize';
  div.style.userSelect = 'none';
  div.style.height = '100%';
  div.classList.add('drag-line');
  return div;
}

function getStyleVal(elm: HTMLElement, css: string) {
  return (window.getComputedStyle(elm, null).getPropertyValue(css));
}

function createIndicatorLine(): HTMLDivElement {
  const line = document.createElement('div');
  line.style.position = 'fixed';
  line.style.width = '2px';
  line.style.background = 'var(--v-default-darken2)';
  line.style.pointerEvents = 'none';
  line.style.zIndex = '10';
  line.style.display = 'none';
  document.body.appendChild(line);
  return line;
}

let sharedIndicator: HTMLDivElement | null = null;
function getIndicatorLine(): HTMLDivElement {
  if (sharedIndicator === null) {
    sharedIndicator = createIndicatorLine();
  }
  return sharedIndicator;
}

let dragCursorStyle: HTMLStyleElement | null = null;
function setDragCursor(active: boolean) {
  if (active && dragCursorStyle === null) {
    dragCursorStyle = document.createElement('style');
    dragCursorStyle.textContent = '* { cursor: col-resize !important; }';
    document.head.appendChild(dragCursorStyle);
  } else if (!active && dragCursorStyle !== null) {
    dragCursorStyle.remove();
    dragCursorStyle = null;
  }
}

function applyColumnWidth(col: HTMLTableColElement, width: number) {
  col.style.width = `${width}px`;
  const headerDiv: HTMLDivElement | null = col.querySelector('.th-header');
  if (headerDiv)
    headerDiv.style.width = `${Math.max(width, 0)}px`;
}

export const resizableColumnsDirective: DirectiveOptions = {
  inserted: (el, bindings) => {
    const bindValues: { skipEdges: boolean, minWidth: number, resized?: () => void } = bindings.value;
    const { skipEdges, minWidth, resized } = bindValues;

    const table = el.querySelector('table');
    if (!table) {
      console.error('v-resizable-columns only valid for TABLE elements!');
      return;
    }

    // CSS in main.style
    table.classList.add('resizable-table');
    const row = table.getElementsByTagName('tr')[0];
    const cols = row ? row.children : undefined;
    if (!cols) {
      return;
    }

    const indicator = getIndicatorLine();

    setTimeout(() => {
      const start = skipEdges ? 1 : 0;
      for (let i = start; i < cols.length; i++) {
        const div = createDiv();
        const col = cols[i] as HTMLTableColElement;
        col.appendChild(div);
        col.style.position = 'sticky';
        setListeners(div, paddingDiff(col));
      }
    }, 1);

    function setListeners(div: HTMLDivElement, padding: number) {
      let pageX: number | undefined;
      let curCol: HTMLTableColElement | undefined;
      let curColWidth: number | undefined;

      const onMouseMove = (e: MouseEvent) => {
        if (curCol && pageX !== undefined && curColWidth !== undefined) {
          const diffX = e.pageX - pageX;
          const clampedWidth = Math.max(curColWidth + diffX, minWidth);
          const clampedDiffX = clampedWidth - curColWidth;
          indicator.style.left = `${pageX + clampedDiffX}px`;
        }
      };

      const onMouseUp = (e: MouseEvent) => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);

        if (curCol && pageX !== undefined && curColWidth !== undefined) {
          const diffX = e.pageX - pageX;
          const newWidth = Math.max(curColWidth + diffX, minWidth);

          applyColumnWidth(curCol, newWidth);
          if (resized)
            resized();
        }
        indicator.style.display = 'none';
        setDragCursor(false);
        curCol = undefined;
        pageX = undefined;
        curColWidth = undefined;
      };

      div.addEventListener('mousedown', e => {
        const targetDiv = e.target as HTMLDivElement;
        curCol = targetDiv.parentElement as HTMLTableColElement;
        pageX = e.pageX;
        curColWidth = curCol.offsetWidth - padding;

        const colRect = curCol.getBoundingClientRect();
        indicator.style.display = 'block';
        indicator.style.top = `${colRect.top}px`;
        indicator.style.height = `${colRect.height}px`;
        indicator.style.left = `${e.pageX}px`;
        setDragCursor(true);

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      div.addEventListener('mouseover', e => {
        const target = e.target as HTMLDivElement | null;
        if (target)
          target.classList.add('drag-hovered');
      });

      div.addEventListener('mouseout', e => {
        const target = e.target as HTMLDivElement | null;
        if (target)
          target.classList.remove('drag-hovered');
      });
    }

    function paddingDiff(col: HTMLTableColElement) {
      const padLeft = getStyleVal(col, 'padding-left');
      const padRight = getStyleVal(col, 'padding-right');
      return (parseInt(padLeft) + parseInt(padRight));
    }
  },
};

export const safeHtmlDirective: DirectiveOptions = {
  bind: (el, binding) => {
    renderSafeHtml(el, binding.value);
  },
  update: (el, binding) => {
    renderSafeHtml(el, binding.value);
  },
};

const renderSafeHtml = (el: HTMLElement, builder: any) => {
  if (!isInstanceOfSafeHtml(builder)) {
    throw new Error('Invalid type provided for v-safe-html. Expected an object of SafeHtmlBuilder instance.');
  }

  el.innerHTML = builder.getSafeHtml();
};

const observers = new WeakMap<HTMLElement, ResizeObserver>();
export const resizeObserverDirective: DirectiveOptions = {
  bind: (el, binding) => {
    const callback = binding.value;
    if (!(callback instanceof Function)) throw `v-onresize received ${typeof callback} ${binding.expression} instead of callback`;
    const resizeObserver = new ResizeObserver(debounce(entries => callback(entries[0].contentRect.width, entries[0].contentRect.height)));
    resizeObserver.observe(el);
    observers.set(el, resizeObserver);
  },
  unbind: (el) => {
    observers.get(el)?.disconnect();
  }
};


type VueInstance = CombinedVueInstance<any, any, any, any, any>;
const tooltips = new WeakMap<HTMLElement, VueInstance>();

export interface TooltipDirectiveConfig {
  disabled?: boolean,
  nudgeRight?: number,
  openDelay?: number,
  text: string,
}

function getTooltipDirectiveConfig(binding: DirectiveBinding): TooltipDirectiveConfig {
  if (isObject(binding.value)) {
    const maybe = binding.value as Partial<TooltipDirectiveConfig>;
    return {
      text: typeof maybe.text === 'string' ? maybe.text : '',
      disabled: Boolean(maybe.disabled),
      nudgeRight: typeof maybe.nudgeRight === 'number' ? maybe.nudgeRight : undefined,
      openDelay: typeof maybe.openDelay === 'number' ? maybe.openDelay : undefined,
    };
  }

  return {
    text: typeof binding.value === 'string' ? binding.value : '',
    disabled: false,
  };
}

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

function createTooltip(el: HTMLElement, binding: DirectiveBinding): VueInstance {
  const tdc = getTooltipDirectiveConfig(binding);
  const position: TooltipPosition | undefined = binding.arg as TooltipPosition ?? 'bottom';

  const Tooltip = Vue.extend(V3Tooltip);
  const instance = new Tooltip({
    propsData: {
      position,
      ...tdc,
      activator: el
    }
  });
  instance.$mount();

  tooltips.set(el, instance);
  return instance;
}

export const tooltipDirective: DirectiveOptions = {
  bind(el, bindings) {
    createTooltip(el, bindings);
  },
  update(el, binding) {
    const tdc = getTooltipDirectiveConfig(binding);
    const instance = tooltips.get(el);

    const wasDisabled = !instance.text || instance.disabled;
    const willHaveText = Boolean(tdc.text);

    instance.text = tdc.text;
    instance.disabled = tdc.disabled;

    // If tooltips are initially empty, undefined or disabled,
    // then reinitialize the tooltip to ensure it is displayed correctly.
    if (wasDisabled && willHaveText) {
      instance.$destroy();
      tooltips.delete(el);
      createTooltip(el, binding);
    }
  },
  unbind(el) {
    tooltips.get(el)?.$destroy();
    tooltips.delete(el);
  }
};
